import React, { createContext, useContext, useState, useEffect } from 'react';
import apiService from '../services/api';
import { saveAuth, clearAuth } from '../utils/authStorage'; // ✅ email-keyed ghost-proof storage

const AuthContext = createContext(null);

// ─── Single source of truth for the token key ─────────────────────────────────
// ALL login methods write here. api.js _getToken() reads 'token' first so this
// guarantees the correct token is always found regardless of login method used.
const TOKEN_KEY = 'token';

// Helper: wipe every token key variant so no stale value lingers after logout.
// ✅ Now delegates to clearAuth() which also removes 'authEmail' — the key that
// prevents ghost-user tokens from surviving a delete-and-recreate cycle.
const clearAllTokenKeys = () => {
  clearAuth(); // removes: token, authToken, auth_token, user, authEmail
  ['adminToken', 'admin_token'].forEach(k => {
    localStorage.removeItem(k);
    sessionStorage.removeItem(k);
  });
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [profileImage, setProfileImage] = useState(null);
  
  // ✅ NEW: Auth version to force re-renders when auth changes
  const [authVersion, setAuthVersion] = useState(0);

  // ─── Restore auth state from localStorage on mount ──────────────────────────
  useEffect(() => {
    const initializeAuth = () => {
      try {
        const token = localStorage.getItem(TOKEN_KEY);
        const savedUser = localStorage.getItem('user');
        const savedIsAdmin = localStorage.getItem('isAdmin');
        const savedProfileImage = localStorage.getItem('profileImage');


        if (token && savedUser) {
          const userData = JSON.parse(savedUser);
          setUser(userData);
          setIsAuthenticated(true);
          setIsAdmin(savedIsAdmin === 'true');

          // ✅ FIX: Prefer the Supabase URL stored on user object over the
          // separate profileImage key (which may hold a stale base64 string).
          // user.profile_image_url is written by updateProfileImage() and
          // survives page refresh as the authoritative image source.
          let restoredImage =
            userData.profile_image_url ||
            userData.profile_image     ||
            savedProfileImage          ||
            null;

          // ── Fix double-URLs cached in localStorage from previous broken uploads ──
          // Uses the same robust pattern as updateProfileImage: find any second 'http'
          // after the first URL's scheme+host, handling both https:// and https:/
          // (percent-encoded colon becomes : not :// after decodeURIComponent).
          if (restoredImage) {
            const decoded = decodeURIComponent(restoredImage);
            const firstSchemeEnd = decoded.indexOf('://');
            if (firstSchemeEnd > 0) {
              const afterFirstHost = decoded.indexOf('/', firstSchemeEnd + 3);
              const secondHttp = afterFirstHost > 0
                ? decoded.indexOf('http', afterFirstHost)
                : -1;
              if (secondHttp > 0) {
                console.warn('⚠️ Cached double-URL fixed on restore');
                restoredImage = decoded.slice(secondHttp);
                try {
                  const fixed = { ...userData, profile_image_url: restoredImage, profile_image: restoredImage };
                  localStorage.setItem('user', JSON.stringify(fixed));
                  localStorage.setItem('profileImage', restoredImage);
                } catch (_) {}
              }
            }
          }

          if (restoredImage) setProfileImage(restoredImage);
          
        } else {
          console.log('⚠️ No auth data found in localStorage');
          setIsAuthenticated(false);
        }
      } catch (error) {
        console.error('❌ Error initializing auth:', error);
        clearAllTokenKeys();
        localStorage.removeItem('user');
        localStorage.removeItem('isAdmin');
        localStorage.removeItem('profileImage');
        setIsAuthenticated(false);
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, []);

  // ─── Shared helper: persist auth data after any login method ────────────────
  const _persistAuth = (token, userData, adminStatus) => {

    // ✅ saveAuth() clears ALL ghost tokens first (including authEmail) then
    // writes the new session keyed by email so delete-and-recreate cycles
    // can never leave stale tokens that fire ghost API requests.
    localStorage.setItem('loginTime', Date.now().toString()); saveAuth(token, userData);

    // Also persist admin flag and profile image key (not handled by saveAuth)
    localStorage.setItem('isAdmin', adminStatus.toString());

  };

  // ─── Login with email/phone + password ──────────────────────────────────────
  const login = async (identifier, password) => {
    try {
      const response = await apiService.loginWithPassword(identifier, password);

      if (response.success && response.token) {
        const adminStatus = response.user?.is_staff || response.user?.is_superuser || false;

        _persistAuth(response.token, response.user, adminStatus);

        // ✅ Update state AND increment version to trigger re-renders
        setUser(response.user);
        setIsAuthenticated(true);
        setIsAdmin(adminStatus);
        setTimeout(() => {
          setAuthVersion(prev => {
          const newVersion = prev + 1;
          return newVersion;
        });
        }, 0);
        
        return { success: true, isAdmin: adminStatus };
      } else {
        console.error('❌ Login failed:', response.error);
        return { success: false, error: response.error || 'Login failed' };
      }
    } catch (error) {
      console.error('❌ Login error:', error);
      return { success: false, error: 'Network error. Please check your connection.' };
    }
  };

  // ─── Login with OAuth token (Google / Facebook) ──────────────────────────────
  const loginWithOAuth = (oauthData) => {
    try {
      const { token, user: userData, isAdmin: adminFromOAuth } = oauthData;

      if (!token || !userData) {
        console.error('❌ OAuth login missing token or user data');
        return { success: false, error: 'Invalid OAuth response' };
      }

      // Normalize OAuth user to match regular login shape
      const normalizedUser = {
        ...userData,
        identifier: userData.email || userData.name || 'User',
        email: userData.email || '',
      };

      const adminStatus =
        adminFromOAuth ||
        userData?.is_admin ||
        userData?.is_staff ||
        userData?.is_superuser ||
        false;

      _persistAuth(token, normalizedUser, adminStatus);

      // ✅ Update state AND increment version
      setUser(normalizedUser);
      setIsAuthenticated(true);
      setIsAdmin(adminStatus);
      setTimeout(() => {
        setAuthVersion(prev => {
        const newVersion = prev + 1;
        return newVersion;
      });
      }, 0);

      return { success: true, isAdmin: adminStatus };
    } catch (error) {
      console.error('❌ OAuth login error:', error);
      return { success: false, error: 'Failed to process OAuth login' };
    }
  };

  // ─── Login with verification code ───────────────────────────────────────────
  const loginWithVerification = async (identifier, code) => {
    try {
      const response = await apiService.verifyCode(identifier, code);

      if (response.token) {
        const adminStatus = response.user?.is_staff || response.user?.is_superuser || false;

        _persistAuth(response.token, response.user, adminStatus);

        // ✅ Update state AND increment version
        setUser(response.user);
        setIsAuthenticated(true);
        setIsAdmin(adminStatus);
        setTimeout(() => {
          setAuthVersion(prev => {
          const newVersion = prev + 1;
          return newVersion;
        });
        }, 0);
        
        return { success: true, isAdmin: adminStatus };
      } else {
        return { success: false, error: response.error || 'Verification failed' };
      }
    } catch (error) {
      console.error('❌ Verification error:', error);
      return { success: false, error: 'Network error' };
    }
  };

  // ─── Logout ──────────────────────────────────────────────────────────────────
  const logout = async () => {
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      if (token) {
        // await apiService.logout(token); // uncomment when ready
      }
    } catch (error) {
      console.error('❌ Logout error:', error);
    } finally {
      // ✅ Wipe every possible token key variant + user data + authEmail
      clearAllTokenKeys(); // calls clearAuth() internally — removes authEmail too
      localStorage.removeItem('isAdmin');
      localStorage.removeItem('profileImage');

      // ✅ Update state AND increment version
      setUser(null);
      setIsAuthenticated(false);
      setIsAdmin(false);
      setProfileImage(null);
      setTimeout(() => {
        setAuthVersion(prev => {
        const newVersion = prev + 1;
        return newVersion;
      });
      }, 0);
    }
  };

  const getToken = () => localStorage.getItem(TOKEN_KEY);

  const updateProfileImage = (imageUrl) => {
    try {
      // ── Sanitize double-URLs before doing anything ────────────────────────
      // A double-URL is produced when Django's FileSystemStorage prepends MEDIA_URL
      // to a Supabase URL already stored in the DB. It comes through percent-encoded:
      //   https://host/media/https%3A/host/profile_images/users/4/profile.jpg
      // After decoding: https://host/media/https:/host/profile_images/...
      // Note the SINGLE slash after https: — the colon is %3A which decodes to ':'
      // not '://', so indexOf('https://', 9) returns -1 and the old sanitizer missed it.
      //
      // Fix: after decoding, find the second occurrence of 'http' (with or without //)
      // by searching past the end of the first URL's scheme+authority.
      let sanitizedUrl = imageUrl;
      if (imageUrl) {
        const decoded = decodeURIComponent(imageUrl);
        // Skip past the first scheme (e.g. "https://host.com") to find any second URL
        const firstSchemeEnd = decoded.indexOf('://');
        if (firstSchemeEnd > 0) {
          // Search for any 'http' that appears after the first host section
          const afterFirstHost = decoded.indexOf('/', firstSchemeEnd + 3); // first path slash
          const secondHttp = afterFirstHost > 0
            ? decoded.indexOf('http', afterFirstHost)
            : -1;
          if (secondHttp > 0) {
            // Preserve any ?t= cache-buster that was on the outer URL
            const qtIdx = imageUrl.indexOf('?t=');
            const cacheBust = qtIdx > 0 ? imageUrl.slice(qtIdx) : '';
            sanitizedUrl = decoded.slice(secondHttp) + cacheBust;
          }
        }
      }

      // Update React state immediately (keep ?t= cache-buster for the <img> tag)
      setProfileImage(sanitizedUrl);

      // Strip ?t= before persisting — store only the clean canonical Supabase URL.
      // On next refresh this clean URL is read back; the next upload adds a fresh ?t=.
      const cleanUrl = sanitizedUrl && sanitizedUrl.includes('?t=')
        ? sanitizedUrl.split('?t=')[0]
        : sanitizedUrl;

      // Write profile_image into the stored user object so it survives page refresh.
      // This is the ONLY place that matters — on mount, initializeAuth() reads
      // userData.profile_image to restore the avatar. If this key is missing the
      // image disappears every refresh.
      setUser(prev => {
        const base = prev || {};
        const updated = {
          ...base,
          profile_image_url: cleanUrl,
          profile_image:     cleanUrl,
        };
        try {
          localStorage.setItem('user', JSON.stringify(updated));
        } catch (_) {}
        return updated;
      });

      // Belt-and-suspenders: also write the standalone profileImage key
      if (cleanUrl) localStorage.setItem('profileImage', cleanUrl);

      return { success: true };
    } catch (error) {
      console.error('❌ Error updating profile image:', error);
      return { success: false, error: 'Failed to update profile image' };
    }
  };

  const value = {
    user,
    isAuthenticated,
    isAdmin,
    loading,
    profileImage,
    authVersion, // ✅ NEW: Expose auth version for components to watch
    login,
    loginWithOAuth,
    loginWithVerification,
    logout,
    getToken,
    updateProfileImage,
    setUser,
    setIsAdmin,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
};

